import axios from 'axios';
import qs from 'qs';
import * as mime from 'mime-types';
import constants from 'utils/constants';
import Initialiser from 'initialiser';
import { getAuthHeader } from 'utils/auth';
import store from 'ducks/configureStore';
import { logout, selectUser } from 'ducks/modules/user';
import { REPORT_TYPES } from '../scenes/matching-dashboard/matching-dashboard.model';
import { generateTraceHeaders } from 'utils/tracing';
import { shouldRefreshAndRetryOn401 } from 'utils/auth-refresh';

class API {
    constructor() {
        this.headers = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };
        this.isRefreshing = false;
    }

    async request(method, url, params, data, authHeaderOverride) {
        try {
            // Generate trace headers for each request
            const traceHeaders = generateTraceHeaders();

            const response = await axios.request({
                method,
                url: `/api/v1${url}`,
                params,
                data,
                headers: Object.assign(this.headers, {
                    Authorization: authHeaderOverride || getAuthHeader(),
                    'X-bk-ignore-events': localStorage.getItem('ignore_events') || 'false',
                    ...traceHeaders,
                }),
            });
            return response.data;
        } catch (error) {
            if (
                !error.response ||
                (error.response && error.response.status === constants.HttpCodes.SERVICE_UNAVAILABLE)
            ) {
                if (Initialiser.history.location.pathname.indexOf('/unavailable') === -1) {
                    Initialiser.history.push({
                        pathname: '/unavailable',
                        search: qs.stringify({
                            from: `${Initialiser.history.location.pathname}${Initialiser.history.location.search}`,
                        }),
                    });
                }
            } else if (error.response && error.response.status === constants.HttpCodes.UNAUTHORIZED) {
                if (shouldRefreshAndRetryOn401(url, authHeaderOverride)) {
                    // try refreshing auth tokens
                    if (!this.isRefreshing) {
                        await this.refreshAuth();
                    } else {
                        // wait for an in-progress refresh to finish
                        await this.refreshDeferred;
                    }
                    // then redo request
                    return this.request(method, url, params, data, authHeaderOverride);
                }
                throw error.response;
            } else if (
                error.response &&
                (error.response.status === constants.HttpCodes.UNAUTHORIZED ||
                    error.response.status === constants.HttpCodes.BAD_REQUEST) &&
                url === '/auth/refresh'
            ) {
                // refresh token was invalid, we need to log out
                store.dispatch(logout(true));
            }

            throw error.response;
        }
    }

    async refreshAuth() {
        this.isRefreshing = true;
        // refreshDeferred can be used externally to wait on the currently running refresh request
        // eslint-disable-next-line no-async-promise-executor
        this.refreshDeferred = new Promise(async (resolve, reject) => {
            if (!localStorage.getItem('refresh_token')) {
                this.isRefreshing = false;
                return reject();
            }
            try {
                const tokens = await this.request(
                    'POST',
                    '/auth/refresh',
                    null,
                    undefined,
                    `Bearer ${localStorage.getItem('refresh_token')}`,
                );
                localStorage.setItem('access_token', tokens.access_token);
                localStorage.setItem('refresh_token', tokens.refresh_token);
                this.isRefreshing = false;
                return resolve();
            } catch (err) {
                this.isRefreshing = false;
                return reject(err);
            }
        });
        return this.refreshDeferred;
    }

    async authenticate(credentials) {
        const user = await this.request('POST', '/auth', null, {
            username: credentials.username,
            password: credentials.password,
        });
        if (user && user.access_token && user.refresh_token) {
            localStorage.setItem('access_token', user.access_token);
            localStorage.setItem('refresh_token', user.refresh_token);
        }

        return user;
    }

    async proxyAuth(who) {
        const user = await this.request('POST', '/auth/proxy', null, { who });
        localStorage.setItem('access_token', user.access_token);
        localStorage.setItem('refresh_token', user.refresh_token);
        return user;
    }

    resetPassword(oldPassword, newPassword, id, token) {
        const payload = { newPassword };
        if (oldPassword) {
            payload.oldPassword = oldPassword;
        } else {
            payload.id = id;
        }
        return this.request('POST', '/auth/reset-password', null, payload, token ? `Bearer ${token}` : null);
    }

    getObjectUrl(s3Uri) {
        const params = new URLSearchParams();
        params.append('s3uri', s3Uri);
        return this.request('GET', `/data/object-url`, params);
    }

    recoverPassword(email) {
        const payload = { email };
        return this.request('POST', '/auth/recover-password', null, payload);
    }

    fetchUser() {
        return this.request('GET', '/auth');
    }

    cancelMatchingDashboardSubscription() {
        return this.request('PUT', `/matching-dashboard/cancel-subscription`);
    }

    getUserActivityTimeline(bkUserId, before = null, limit = null) {
        return this.request('GET', `/users/${bkUserId}/activity`, {
            ...(before && { before }),
            ...(limit && { limit }),
        });
    }

    fetchDashboardStats(bkUserId, range) {
        return this.request('GET', `/users/${bkUserId}/stats?range=${range}`);
    }

    fetchDashboardActions(bkUserId) {
        return this.request('GET', `/users/${bkUserId}/suggested-actions`);
    }

    fetchWorkDetails(bkWorkId) {
        return this.request('GET', `/works/${bkWorkId}`);
    }

    fetchWorkMetadata(bkWorkId) {
        return this.request('GET', `/works/${bkWorkId}/metadata`);
    }

    fetchWorkRecordings(bkWorkId) {
        return this.request('GET', `/works/${bkWorkId}/recordings`);
    }

    fetchWorkActivity(bkWorkId, offset, limit) {
        return this.request('GET', `/works/${bkWorkId}/activity?offset=${offset}&limit=${limit}`);
    }

    fetchIPIRollup(ipi) {
        return this.request('GET', `/rollups/${ipi}`);
    }

    fetchSearchIPIs(ipi) {
        return this.request('GET', `/rollups/${ipi}/search`);
    }

    searchWorks(offset, limit, data) {
        return this.request('GET', `/works/search?offset=${offset}&limit=${limit}&${qs.stringify(data)}`);
    }

    searchImpelWorks(page_number, limit, data) {
        return this.request(
            'GET',
            `/works/search/impel?page_number=${page_number}&limit=${limit}&${qs.stringify(data)}`,
        );
    }

    fetchImpelSocieties() {
        return this.request('GET', `/mandates/societies`);
    }

    downloadWorkList(filter) {
        return this.request('GET', `/works/download?filter=${filter}`);
    }

    fetchClaimSocietyEmails() {
        return this.request('GET', `/content/society-emails`);
    }

    getLongrunningOperation(operationName) {
        return this.request('GET', `/matching-dashboard/get-operation/?operation_name=${operationName}`);
    }

    fetchWorkClaims(bkWorkId, country, status) {
        return this.request(
            'GET',
            `/works/${bkWorkId}/claims?state=O${country ? `&country=${country}` : ''}${
                status ? `&status=${status}` : ''
            }`,
        );
    }

    fetchResolvedWorkClaims(bkWorkId, country, societyEmailsSent = null) {
        return this.request(
            'GET',
            `/works/${bkWorkId}/claims?state=A${country ? `&country=${country}` : ''}${
                societyEmailsSent !== null ? `&societyEmailsSent=${societyEmailsSent}` : ''
            }`,
        );
    }

    reviewResolvedClaim(bkWorkId, claimId, previewEmail) {
        return this.request('POST', `/works/${bkWorkId}/claims/${claimId}/review`, null, { previewEmail });
    }

    createClaim(bkWorkId, data) {
        return this.request('POST', `/works/${bkWorkId}/claims`, null, data);
    }

    withdrawClaim(bkWorkId, claimId) {
        return this.request('PUT', `/works/${bkWorkId}/claims/${claimId}`, null, { state: 'W' });
    }

    submitClaimResponse(bkWorkId, claimId, data) {
        return this.request('POST', `/works/${bkWorkId}/claims/${claimId}/responses`, null, data);
    }

    updateResponse(bkWorkId, claimId, responseId, data) {
        return this.request('PUT', `/works/${bkWorkId}/claims/${claimId}/responses/${responseId}`, null, data);
    }

    getDataUploadURL(filenames) {
        const params = new URLSearchParams();
        filenames.forEach((f) => params.append('filename', f));

        return this.request('GET', `/data/upload-url`, params.toString());
    }

    getLicenceUploadURL(filenames, bkWorkId) {
        const params = new URLSearchParams();
        params.append('bkWorkId', bkWorkId);
        filenames.forEach((f) => params.append('filename', f));

        return this.request('GET', `/data/upload-url?${params.toString()}`);
    }

    matchingDashboardSubscribe(stripeCheckoutSessionId, query) {
        return this.request('POST', '/matching-dashboard/subscribe', query || null, {
            stripe_checkout_session_id: stripeCheckoutSessionId,
        });
    }

    getMatchingUsageUploadURL(filename, reportType) {
        const params = new URLSearchParams();
        params.append('filename', filename);
        params.append('type', 'DATA');
        params.append('sub-folder', 'matching_dashboard');
        if (reportType) {
            params.append('report-type', reportType);
        }

        return this.request('GET', `/data/upload-url?${params.toString()}`);
    }

    getReporterServiceUploadURL(reportType) {
        return this.request('GET', `/data/upload-url`, { reportType });
    }

    /**
     *
     * @param {string} filename
     * @param {*} headersMapping
     * @param {*} reportVariation
     * @returns
     */
    submitHeadersMapping(filename, headersMapping, reportType, reportVariation, royaltyRate, ddexDsrDetails) {
        const royaltyEndpoint = royaltyRate?.options?.skip_royalty_computation ? 'no-usage-royalty' : 'royalty';
        switch (reportType) {
            case REPORT_TYPES.PUBLISHING_LABEL:
                return this.request('POST', `/matching-dashboard/submit`, null, {
                    filename,
                    type: 'DATA',
                    headers_mapping: headersMapping,
                    labelToggle: true,
                });
            case REPORT_TYPES.ROYALTY:
                return this.request('POST', `/matching-dashboard/report/${royaltyEndpoint}`, null, {
                    fileUri: filename,
                    reportType: {
                        royalty: {
                            inputs: royaltyRate,
                            headers: headersMapping,
                            adaptiveQuestionValues: {
                                rightType: '', // A list of strings, but also custom any input
                                tier: '', // Any string
                                territory: '', // TIS territory name, use column j (name) from this list: https://docs.google.com/spreadsheets/d/1MtTe0QrPo1SKA5a13eWHbHhpt_-WVF-K4hMEVlm6Yf8/edit#gid=1240993035
                                currency: '', // 3 letter code found here https://www.iban.com/currency-codes
                                periodStart: '', // YYYY MM DD
                                periodEnd: '', // YYYY MM DD
                            },
                        },
                    },
                });
            case REPORT_TYPES.DDEX:
                return this.request('POST', `/matching-dashboard/report/ddex-dsr`, null, {
                    filename,
                    type: 'DATA',
                    ddexDsrDetails,
                    headers_mapping: headersMapping,
                });
            default:
                return this.request('POST', `/matching-dashboard/submit`, null, {
                    filename,
                    type: 'DATA',
                    headers_mapping: headersMapping,
                });
        }
    }

    /**
     * Used for communicating with the reporterService
     * @param {String} filename
     * @param {String} reportType
     * @param {Object} reportVariation
     * @returns
     */
    submitReport(filename, reportType, reportVariation) {
        return this.request('POST', `/matching-dashboard/report/${reportType}`, null, {
            fileUri: filename,
            [reportType]: {
                ...reportVariation,
            },
        });
    }

    /**
     * Used for communicating with the reporterService
     * @param {String} fileUri
     * @param {Number} territory
     * @param {Object} headers
     * @param {String?} headers.isrc
     * @param {String?} headers.artist
     * @param {String?} headers.title
     * @param {String?} headers.unique_id
     * @returns
     */
    submitAnuvuReport(fileUri, territory, headers, totalLines) {
        return this.request('POST', `/matching-dashboard/report/anuvu`, null, {
            fileUri,
            reportType: {
                anuvu: {
                    territory,
                    headers,
                },
            },
            metadata: {
                totalLines: totalLines,
            },
        });
    }

    async getMatchingUsageHeaders(filename, reportType = REPORT_TYPES.UNKNOWN, rightsHolder = null) {
        try {
            const params = new URLSearchParams();
            params.append('filename', filename);

            if (reportType) {
                params.append('report-type', reportType);
            }

            if (rightsHolder) {
                params.append('rights-holder', rightsHolder);
            }

            const { convertedCsvS3Url, contents, stats } = await this.request(
                'GET',
                `/matching-dashboard/preview-file`,
                params,
            );

            if (convertedCsvS3Url) {
                const fileUri = convertedCsvS3Url.replace(/^s3:\/\//, '').split('/');
                fileUri.shift(); // remove bucket
                filename = fileUri.join('/');
            }

            return {
                convertedCsvS3Url: filename,
                contents,
                stats,
            };
        } catch (error) {
            console.error('Error fetching matching usage headers', error);
            throw error;
        }
    }

    uploadFile(url, file) {
        const contentType = mime.lookup(file.name) || 'application/octet-stream';
        return axios.put(url, file, {
            headers: { 'Content-Type': contentType },
        });
    }

    async verifyDownloadUrl(url) {
        try {
            const res = await axios.get(url, { headers: { Range: 'bytes=0-0' } });
            if (res.status >= 200 && res.status < 300) {
                return true;
            }
            throw Error(res.statusText);
        } catch {
            return false;
        }
    }

    createMatchingDashboardHubspotContact(email) {
        return this.request('POST', '/matching-dashboard/submit-public-user-email', null, { email });
    }

    createHubspotMobileToDesktopContact({ email, context }) {
        return axios.post(
            `https://api.hsforms.com/submissions/v3/integration/submit/${process.env.HUBSPOT_PORTAL_ID}/${process.env.HUBSPOT_DND_MOBILE_FORM_ID}`,
            {
                submittedAt: Date.now(),
                fields: [
                    {
                        name: 'email',
                        value: email,
                    },
                ],
                context,
            },
        );
    }

    getCustomerSubcriptionPortalUrl() {
        return this.request('GET', '/matching-dashboard/subscription-portal');
    }

    getForm(form) {
        return this.request('GET', `/forms/${form}`);
    }

    acceptForm(form) {
        return this.request('POST', `/forms/${form}`, null, { accept: true });
    }

    rejectForm(form) {
        return this.request('POST', `/forms/${form}`, null, { accept: false });
    }

    acceptState(bkWorkId, countries) {
        return this.request('POST', `/works/${bkWorkId}/state/accept`, null, {
            bk_work_id: bkWorkId,
            countries,
        });
    }

    undoAcceptState(bkWorkId, countries) {
        return this.request('POST', `/works/${bkWorkId}/state/accept/undo`, null, {
            bk_work_id: bkWorkId,
            countries,
        });
    }

    removeWorkFromCatalogue(bkWorkId) {
        return this.request('POST', `/works/${bkWorkId}/mention/remove`, null, {
            bk_work_id: bkWorkId,
        });
    }

    fetchUsers(searchBy, searchTerm) {
        return this.request('GET', `/users?${qs.stringify({ searchBy, searchTerm })}`);
    }

    fetchApiEndpoints() {
        return this.request('GET', `/api-endpoints`);
    }

    fetchUserApiTokens(bkUserId) {
        return this.request('GET', `/users/${bkUserId}/api-tokens`);
    }

    createUserApiToken(bkUserId, accessibleEndpoints) {
        return this.request('POST', `/users/${bkUserId}/api-tokens`, null, {
            accessible_endpoints: accessibleEndpoints,
        });
    }

    updateUserApiTokenEndpoints(bkUserId, accessibleEndpoints) {
        return this.request('PUT', `/users/${bkUserId}/api-tokens/endpoints`, null, {
            accessible_endpoints: accessibleEndpoints,
        });
    }

    fetchSourceUser(bkUserId) {
        return this.request('GET', `/users/${bkUserId}`);
    }

    fetchUserResetPasswordToken(id) {
        return this.request('GET', `/users/accounts/${id}/reset-password-token`);
    }

    updateUser(id, data) {
        return this.request('PUT', `/users/accounts/${id}`, null, data);
    }

    createUserAccount(bkUserId, data) {
        return this.request('POST', `/users/accounts`, null, Object.assign(data, { bkUserId }));
    }

    /**
     *
     * @param {Object} data
     * @param {string} data.firstName
     * @param {string} data.lastName
     * @param {string} data.email
     * @param {string} data.company
     * @param {string} data.company_name
     * @returns {Promise}
     */
    createMusicUserAccount(data) {
        const display_name = `${data.firstName} ${data.lastName}`;
        return this.request('POST', `/users/accounts/signup`, null, { display_name, ...data });
    }

    /**
     *
     * @param {Object} data
     * @param {string} data.firstName
     * @param {string} data.lastName
     * @param {string} data.email
     * @param {string} data.company
     * @param {string} data.company_name
     * @returns {Promise}
     */
    createMatchingDashboardAccount(data) {
        const display_name = data.firstName.charAt(0).toUpperCase() + data.lastName.charAt(0).toUpperCase();
        const signupType = 'matching_dashboard';
        return this.request('POST', `/users/accounts/signup`, null, { display_name, signupType, ...data });
    }

    /**
     *
     * @param {Object} data
     * @param {string} data.firstName
     * @param {string} data.lastName
     * @param {string} data.email
     * @param {string} data.company
     * @param {string} data.company_name
     * @returns {Promise}
     */
    createDSPAccount(data) {
        const display_name = `${data.firstName} ${data.lastName}`;
        const signupType = 'dsp';
        return this.request('POST', `/users/accounts/signup`, null, { display_name, signupType, ...data });
    }

    createMusicUser(name, displayPicture) {
        return this.request('POST', `/users/music`, null, { name, display_picture: displayPicture });
    }

    bulkCreateUserAccounts(data) {
        return this.request('POST', `/users/accounts?bulk=true`, null, { users: data });
    }

    generateResetPasswordToken(id) {
        return this.request('POST', `/users/accounts/${id}/generate-reset-password-token`, null, {});
    }

    fetchNotifications(accountId, minDate, maxDate, limit) {
        let queryParams = '';
        if (minDate !== null) {
            queryParams += '&minDate=' + minDate.toISOString();
        }
        if (maxDate !== null) {
            queryParams += '&maxDate=' + maxDate.toISOString();
        }
        return this.request('GET', `/users/accounts/${accountId}/notifications?limit=${limit}${queryParams}`, null, {});
    }
    readAllNotifications(accountId) {
        return this.request('POST', `/users/accounts/${accountId}/notifications/read`, null, {});
    }
    readNotification(accountId, notificationId) {
        return this.request('POST', `/users/accounts/${accountId}/notifications/${notificationId}/read`, null, {});
    }

    fetchPerformanceCharts() {
        return this.request('GET', `/charts/performance`);
    }

    fetchPerformanceTopSongs(slug, startDate, endDate) {
        return this.request(
            'GET',
            `/charts/performance/top-songs?chartSlug=${slug}&startDate=${startDate}&endDate=${endDate}`,
        );
    }
    fetchPerformanceMarketShare(slug, startDate, endDate) {
        return this.request(
            'GET',
            `/charts/performance/market-share?chartSlug=${slug}&startDate=${startDate}&endDate=${endDate}`,
        );
    }
    fetchRankingsLatestDate(slug) {
        return this.request('GET', `/charts/performance/rankings/latest?chartSlug=${slug}`);
    }
    fetchPerformanceRankings(slug, endDate) {
        return this.request(
            'GET',
            `/charts/performance/rankings?chartSlug=${slug}&endDate=${endDate}&hasInterval=true`,
        );
    }
    fetchInbox(user) {
        return this.request(
            'GET',
            `/messages/${user.bk_user_id}/${user.id}${user.source_type === 'M' ? '?sent=true' : ''}`,
            null,
            { username: user.username },
        );
    }
    createLicenceRequest(messages, unidentifiedShares, fullRequest) {
        return this.request('POST', `/messages`, null, { messages, unidentifiedShares, fullRequest });
    }
    fetchLicenceRecipients(ipis) {
        return this.request('GET', `/messages/ipis?accounts=true&${qs.stringify({ ipis })}`);
    }
    searchMessages(user, term) {
        return this.request('GET', `/messages/search?term=${term}&bkUserId=${user.bk_user_id}&bkAccountId=${user.id}`);
    }
    fetchMessage(recipient, id) {
        return this.request('GET', `/messages/${recipient.bk_user_id}/${recipient.bk_account_id}/${id}`);
    }
    createMessageComment(messageId, user, recipient, comment, files) {
        return this.request(
            'POST',
            `/messages/${messageId}`,
            null,
            Object.assign(
                {},
                {
                    comment,
                    bk_user_id: user.bk_user_id,
                    account_id: user.id,
                    recipient_bk_user_id: recipient.bk_user_id,
                    recipient_account_id: recipient.bk_account_id,
                    files,
                },
            ),
        );
    }
    markMessageAsRead(message) {
        return this.request(
            'POST',
            `/messages/${message.id}/markAsRead`,
            null,
            Object.assign({}, { bk_user_id: message.recipient.bk_user_id, bk_account_id: message.recipient.id }),
        );
    }
    getRoyalties(aggregation, filter) {
        const filterQuery = qs.stringify(filter);
        return this.request('GET', `/royalties?aggregation=${aggregation}${filterQuery ? `&${filterQuery}` : ''}`);
    }

    getFileStatus() {
        return this.request('GET', `/data/files`);
    }

    refreshFileDownloadUrl(file, options = { isAck: false, disableCache: false }) {
        return this.request(
            'POST',
            '/data/refresh-file',
            { ack: options.isAck, ['disable-cache']: options.disableCache },
            { file },
        );
    }

    fetchAckStatus() {
        return this.request('GET', '/data/ack');
    }

    /**
     *
     * @param {number} bkUserId
     * @returns {Promise}
     */
    fetchPublisherMandates(bkUserId) {
        return this.request('GET', `/mandates/${bkUserId}`);
    }

    fetchAllUsersMandates() {
        return this.request('GET', `/mandates/`);
    }

    fetchDSPLicensors() {
        const user = selectUser();
        const bkUserId = user?.bk_user_id;
        if (!bkUserId) {
            throw new Error('No user found');
        }
        return this.request('GET', `/users/${bkUserId}/licensors`);
    }

    postMandates(mandates) {
        return this.request('POST', '/mandates/', null, { mandates: mandates });
    }

    ping() {
        return this.request('GET', `/ping`);
    }

    /**
     *
     * @param {string} channel Channel to send the message to
     * @param {string} color Color of the notification
     * @param {string} text Primary notification message
     * @param {object} fields Extra data to be included in the message
     * @returns {Promise}
     */
    slackBot(channel, color, text, fields) {
        return this.request('POST', `/webhooks/slack`, null, { channel, color, text, fields });
    }
}

export default new API();
